<template>
  <div class="mouse px">
    <step-loader 
      :visible="stepLoaderVisible" 
      :steps="stepLoaderSteps" 
      :currentStep="stepLoaderStep" 
    />
    <c-mouse-am35-electricity
      ref="am35Electricity"
      :showBattery="true"
      :showDongle="this.connectedWay === 'dongle'"
    />
    <div class="fixed-panel px">
      <div class="mouse-panel info" :style="mouseBackground">
        <div
          class="keys"
          :style="{
            transform: `rotateX(${rotateX}) rotateY(${rotateY})`,
            transformOrigin: 'center',
          }"
        >
          <div
            class="key"
            v-for="(key, i) in keys"
            :key="key.key"
            :class="{
              ['key' + (i + 1)]: true,
              active: key.key === activeKey,
              disabled: keyDisabled(key.key),
            }"
            @click="onMouseClick(key.key)"
          >
            {{ keyDesc(key.key) }}
          </div>
        </div>
      </div>
      <div class="radio-panel">
        <div class="tab-group function-tabs">
          <a-radio-group class="virtual-radio" v-model="type" size="large">
            <a-radio-button key="keymap" value="keymap">
              {{ $t("keymap") }}
            </a-radio-button>
            <a-radio-button key="macro" value="macro">
              {{ $t("macro") }}
            </a-radio-button>
            <a-radio-button key="dpi" value="dpi">
              DPI
            </a-radio-button>
            <a-radio-button key="mouseArgs" value="mouseArgs">
              {{ $t("mouse.param") }}
            </a-radio-button>
            <a-radio-button key="light" value="light">
              {{ $t("mouse.light") }}
            </a-radio-button>
            <a-radio-button key="reset" value="reset" @click="onReset">
              {{ $t("reset") }}
            </a-radio-button>
          </a-radio-group>
        </div>
        <div class="tab-group profile-action-tabs">
          <a-dropdown
            class="profile-dropdown"
            :trigger="['click']"
            overlayClassName="am35-profile-dropdown"
            transitionName="slide-up"
          >
            <div
              class="profile-trigger tab-trigger"
              :class="{ disabled: isProfileSwitching }"
              @click.prevent
            >
              <span class="profile-trigger-label">{{ currentProfileLabel }}</span>
              <span class="profile-trigger-arrow">▼</span>
            </div>
            <div slot="overlay" class="am35-profile-menu">
              <div
                class="am35-profile-switch-row"
                :class="{ disabled: isProfileSwitching }"
                @click.stop
                @mousedown.stop
              >
                <span class="am35-profile-switch-label">
                  {{ $t("mouse.comboProfileSwitch") }}
                </span>
                <md-switch
                  class="md-primary am35-profile-switch-control"
                  v-model="am35ProfileKeySwitchEnabled"
                  :disabled="isProfileSwitching"
                ></md-switch>
              </div>
              <div
                v-for="profile in profileMenuOptions"
                :key="profile.value"
                class="am35-profile-menu-item"
                :class="{
                  active: profile.value === selectedAM35Profile,
                  disabled: isProfileSwitching,
                }"
                @click="onSwitchProfile(profile.value)"
              >
                <span class="am35-profile-menu-dot" :class="profile.dotClass"></span>
                <span class="am35-profile-menu-text">{{ profile.menuLabel }}</span>
              </div>
            </div>
          </a-dropdown>
          <a-radio-group class="virtual-radio action-radio" v-model="type" size="large">
            <a-radio-button key="upload" value="upload" @click="onUpload">
              <a-tooltip>
                <template slot="title">
                  <span style="color: #fff;">{{ $t("downloadTip") }}</span>
                </template>
                {{ $t("import") }}
              </a-tooltip>
            </a-radio-button>
            <a-radio-button key="download" value="download" @click="selectVisible = true">
              <a-tooltip>
                <template slot="title">
                  <span style="color: #fff;">{{ $t("saveTip") }}</span>
                </template>
                {{ $t("export") }}
              </a-tooltip>
              <a-modal
                v-model="selectVisible"
                width="80vw"
                :footer="null"
                :centered="true"
                :closable="false"
                :maskClosable="false"
                :maskStyle="{ background: '#1c1c1c' }"
              >
                <div class="c-select">
                  <div class="title">
                    {{ $t("selectVersion.select") }}
                  </div>
                  <md-checkbox v-model="selectKeymap" class="checkbox">
                    {{ $t("keymap") }}
                  </md-checkbox>
                  <md-checkbox v-model="selectMacro" class="checkbox">
                    {{ $t("macro") }}
                  </md-checkbox>
                  <!-- <md-checkbox v-model="selectDpi" class="checkbox">
                    DPI
                  </md-checkbox> -->
                  <md-checkbox v-model="selectParam" class="checkbox">
                    {{ $t("mouse.param") }} & {{ $t("mouse.light") }}
                  </md-checkbox>
                  <div class="buttons">
                    <md-button class="button" @click="selectVisible = false">
                      {{ $t("cancel") }}
                    </md-button>
                    <md-button class="button" @click="onDownload">
                      {{ $t("save") }}
                    </md-button>
                  </div>
                </div>
              </a-modal>
            </a-radio-button>
          </a-radio-group>
        </div>
      </div>
    </div>
    <div class="settings-panel">
      <div class="panels" v-if="type === 'keymap'">
        <div class="panel" :class="{ keybord: mouseOrKeybord === 'keybord' }">
          <div class="panel-content">
            <div class="title">
              <div
                class="half"
                :class="{ active: mouseOrKeybord === 'mouse' }"
                @click="mouseOrKeybord = 'mouse'"
              >
                {{ $t("mouse.mouseKeys") }}
              </div>
              <div
                class="half"
                :class="{ active: mouseOrKeybord === 'keybord' }"
                @click="mouseOrKeybord = 'keybord'"
              >
                {{ $t("mouse.keybordKey") }}
              </div>
            </div>
            <a-radio-group
              :value="selectedKey"
              @change="onSelectedKey"
              class="radio-group"
              v-if="mouseOrKeybord === 'mouse'"
            >
              <a-radio
                class="radio"
                v-for="key in mouseKeys"
                :key="key.key.toString()"
                :value="key.key.toString()"
              >
                {{ $t("mouse.macroDesc." + key.desc) }}
              </a-radio>
            </a-radio-group>

            <!-- 
              AM35 虚拟键盘功能说明:
              - 当前仅保留组件以备未来扩展
              - 实际使用的按键类型: 鼠标按键(1-13) + 基础功能键(6,HID) + 宏按键(8)
              - 组合键功能已禁用 (hotkeyOrMacro === 'hotkey' && false)
              - selectedKey 条件判断: 仅当 type=6 时读取 [1] 索引，避免与其他类型数据冲突
            -->
            <c-mouse-am35-virtual-keyboard
              v-if="mouseOrKeybord === 'keybord'"
              :selectedKey="getVirtualKeyboardSelectedKey()"
              @change="onSelectedKeyboard($event)"
            />
          </div>
        </div>
        <div class="panel">
          <div class="panel-content">
            <div class="title">{{ $t("mouse.basicKeys") }}</div>
            <a-radio-group :value="selectedKey" @change="onSelectedKey" class="radio-group">
              <a-radio
                class="radio"
                v-for="key in basicKeys"
                :key="key.key.toString()"
                :value="key.key.toString()"
                :checked="isKeySelected(key.key.toString())"
              >
                {{ getBasicKeyLabel(key) }}
              </a-radio>
            </a-radio-group>
          </div>
        </div>
        <div class="panel" :class="{ hotkey: hotkeyOrMacro === 'hotkey' }">
          <div class="panel-content">
            <div class="title">
              <div
                class="half"
                :class="{ active: hotkeyOrMacro === 'hotkey' }"
                @click="hotkeyOrMacro = 'hotkey'"
              >
                {{ $t("mouse.hotkey") }}
              </div>
              <div
                class="half"
                :class="{ active: hotkeyOrMacro === 'macro' }"
                @click="hotkeyOrMacro = 'macro'"
              >
                {{ $t("mouse.macro") }}
              </div>
            </div>
            <!-- 
              组合键(Hotkey)功能已禁用:
              - AM35 不支持 Ctrl+A、Shift+B 等组合键配置
              - 相关 UI 通过 v-if="false" 隐藏
              - 火力按键(Firepower)配置也包含在此模板中
            -->
            <template v-if="hotkeyOrMacro === 'hotkey'">
              <div class="checkbox-group">
                <md-checkbox class="checkbox" v-model="comboCtrl">
                  Ctrl
                </md-checkbox>
                <md-checkbox class="checkbox" v-model="comboShift">
                  Shift
                </md-checkbox>
                <md-checkbox class="checkbox" v-model="comboAlt">
                  Alt
                </md-checkbox>
                <md-checkbox class="checkbox" v-model="comboWin">
                  Win
                </md-checkbox>
              </div>

              <span style="font-size: 0.3rem;">+</span>
              <c-mouse-am35-virtual-keyboard
                :selectedKey="comboVirtualKey"
                @change="onComboVirtualKeyboardChange($event)"
              />
              <!-- <div class="sub-title">
                {{ $t("mouse.firepowerKey") }}
              </div>
              <c-mouse-virtual-keyboard
                :selectedKey="keyMatrix[activeKey][0] === '0x0b' ? keyMatrix[activeKey][1] : null"
                @change="onSelectedFirepower(1, $event)"
                showMouse
              />
              {{ $t("mouse.interval") }}
           
              <md-field class="repeat">
                <md-input
                  class="repeat"
                  :value="
                    keyMatrix[activeKey][0] === '0x0b' ? Number(keyMatrix[activeKey][2]) : null
                  "
                  @change="onSelectedFirepower(2, $event.target.value)"
                  type="number"
                  min="1"
                  max="255"
                  step="1"
                  oninput="this.value = this.value.replace(/[^0-9]/g, '');
                  this.value = this.value > 255 ? 255 : this.value;
                  this.value = this.value < 1 ? 1 : this.value;"
                ></md-input>
                <span class="md-suffix">ms</span>
              </md-field>
              <span style="color: transparent;">---</span>
              <a-popover placement="top">
                <template slot="content">
                  <div class="name-question">
                    {{ $t("mouse.repeatTip") }}
                  </div>
                </template>
                {{ $t("mouse.repeat") }}
              </a-popover>
              <md-field class="repeat">
                <md-input
                  class="repeat"
                  :value="
                    keyMatrix[activeKey][0] === '0x0b' ? Number(keyMatrix[activeKey][3]) : null
                  "
                  @change="onSelectedFirepower(3, $event.target.value)"
                  type="number"
                  min="0"
                  max="255"
                  step="1"
                  oninput="this.value = this.value.replace(/[^0-9]/g, '');
                  this.value = this.value > 255 ? 255 : this.value;
                  this.value = this.value < 0 ? 0 : this.value;"
                ></md-input>
              </md-field> -->
            </template>
            <!-- 
              宏按键配置区域:
              - 格式: [8, repeat_mode, repeat_times, macro_name, keyOrder数组]
              - repeat_mode: 0=重复N次, 1=按住触发, 2=自动循环
              - repeat_times: 重复次数 (仅当 repeat_mode=0 时有效)
            -->
            <template v-if="hotkeyOrMacro === 'macro'">
              <a-radio-group
                :value="selectedMacroName"
                @change="onSelectedMacro"
                class="radio-group"
              >
                <a-radio
                  class="radio"
                  v-for="macro in macrosForBinding"
                  :key="macro.macro_name"
                  :value="macro.macro_name"
                >
                  {{ getMacroOptionLabel(macro) }}
                </a-radio>
                <div
                  v-if="!macrosForBinding || macrosForBinding.length === 0"
                  style="text-align: center; padding: 20px; color: #aaa;"
                >
                  {{ $t("macro") }}列表为空
                </div>
              </a-radio-group>
              <a-radio-group
                :value="repeatMode"
                @change="onSelectedRepeatMode"
                class="play-model"
                v-show="shouldShowMacroRepeatModeControls"
              >
                <a-radio
                  v-if="shouldShowMacroRepeatCountControls"
                  class="radio"
                  :value="0"
                >
                  <a-popover placement="top">
                    <template slot="content">
                      <div class="name-question">
                        {{ $t("mouse.repeatTip") }}
                      </div>
                    </template>
                    {{ $t("mouse.repeat") }}
                  </a-popover>
                  <md-field class="repeat">
                    <md-input
                      class="repeat"
                      :value="repeatTimesInput"
                      @click.stop
                      @mousedown.stop
                      @focus="onRepeatTimesFocus"
                      @input="onRepeatTimesInput"
                      @change="onRepeatTimesChange"
                      :disabled="repeatMode != 0"
                      min="1"
                      max="65535"
                      step="1"
                    ></md-input>
                  </md-field>
                </a-radio>
                <a-radio class="radio" :value="1">
                  <a-popover placement="top">
                    <template slot="content">
                      <div class="name-question">
                        {{ $t("mouse.pressTip") }}
                      </div>
                    </template>
                    {{ $t("mouse.press") }}
                  </a-popover>
                </a-radio>
                <a-radio class="radio" :value="2">
                  <a-popover placement="top">
                    <template slot="content">
                      <div class="name-question">
                        {{ $t("mouse.autoTip") }}
                      </div>
                    </template>
                    {{ $t("mouse.auto") }}
                  </a-popover>
                </a-radio>
              </a-radio-group>
            </template>
          </div>
        </div>

        <div class="buttons">
          <div class="button" @click="onDisable">{{ $t("mouse.disable") }}</div>
          <div class="button" @click="onRestore">{{ $t("mouse.restore") }}</div>
        </div>
      </div>
      <c-mouse-am35-macro v-if="type === 'macro'" />
      <c-mouse-am35-dpi v-if="type === 'dpi'" />
      <c-mouse-am35-param v-if="type === 'mouseArgs'" />
      <device-lights-container v-if="type === 'light'" />
    </div>
  </div>
</template>
<script>
import { mapState } from "vuex";
import { keys, mouseKeys, basicKeys, allKeys } from "./mouse";
import VirtualKeyboard from "./components/VirtualKeyboard";
import * as VKB from "./components/VirtualKeyboard"; // 虚拟键盘按键数据
import DeviceLightsContainer from "./components/DeviceLightsContainer";
import StepLoader from "./components/StepLoader";
import stepLoaderMixin from "@/mixins/stepLoaderMixin";
import { deviceCallbackManager } from "@/utils/DeviceCallbackManager";
import { parseAM35SN, getAM35MianImageKey } from "@/utils/businessUtils";
import {
  parseKeyMatrixFromDevice,
  mergeMouseMacroDataFromKeyMatrix,
} from "./utils/protocolAdapter";

export default {
  mixins: [stepLoaderMixin],
  components: {
    VirtualKeyboard,
    StepLoader,
    DeviceLightsContainer,
  },
  data() {
    return {
      keys,
      mouseKeys,
      basicKeys,
      allKeys,
      rotateX: "0deg",
      rotateY: "0deg",
      type: "keymap",
      activeKey: "0x02",
      selectedKey: "0x00",
      mouseOrKeybord: "mouse",
      hotkeyOrMacro: "hotkey",
      selectedMacroName: "",
      repeatMode: 0,
      repeatTimes: 1,
      repeatTimesInput: "1",
      macroLoopInterval: 50,
      macroLoopIntervalInput: "50",
      selectVisible: false,
      selectKeymap: true,
      selectMacro: true,
      selectDpi: true,
      selectParam: true,
      profileOptions: [
        { value: 0, label: "配置1" },
        { value: 1, label: "配置2" },
        { value: 2, label: "配置3" },
        // { value: 3, label: "配置4" },
      ],
      pendingProfileValue: null,
      // 导出结果状态：'success' | 'cancelled' | null
      exportResult: null,
      // 导入结果状态：'success' | 'fail' | null
      importResult: null,
      // 导入流程兜底定时器
      importFlowTimer: null,
      importFlowCloseTimer: null,
      importCancelFallbackTimer: null,
      // 初始化数据超时定时器
      initDataTimer: null,
    };
  },
  computed: {
    ...mapState("assets", {
      images: "images",
    }),
    ...mapState("loader", {
      inLoader: "inLoader",
      systemInfo: "systemInfo",
      currentDevice: "currentDevice",
      isWeb: "isWeb",
      isQT: "isQT",
    }),
    ...mapState("mouseAM35", {
      // profile: "profile",
      keyMatrix: "keyMatrix",
      macroData: "macroData",
      mouseMacroData: "mouseMacroData",
      mouseArgs: "mouseArgs",
      lightData: "lightData",
    }),
    macrosForBinding() {
      if (this.macroData?.length) {
        return this.macroData;
      }
      return this.mouseMacroData || [];
    },
    selectedBindingMacro() {
      if (!this.selectedMacroName) {
        return null;
      }
      return (
        this.findUiMacroByName(this.selectedMacroName) ||
        this.findMacroByName(this.selectedMacroName) ||
        null
      );
    },
    shouldShowMacroRepeatModeControls() {
      if (!this.selectedMacroName) {
        return false;
      }
      return Boolean(this.selectedBindingMacro);
    },
    shouldShowMacroRepeatCountControls() {
      return Boolean(this.selectedBindingMacro);
    },
    selectedAM35Profile() {
      return Number(this.mouseArgs?.currentProfile ?? 0);
    },
    profileMenuOptions() {
      const profileModeMeta = {
        0: { label: this.$t("mouse.profileModes.daily"), dotClass: "green" },
        1: { label: this.$t("mouse.profileModes.game"), dotClass: "yellow" },
        2: { label: this.$t("mouse.profileModes.burst"), dotClass: "red" },
      };
      return this.profileOptions.map((profile) => {
        const meta = profileModeMeta[profile.value] || profileModeMeta[0];
        return {
          ...profile,
          menuLabel: `${profile.label}-${meta.label}`,
          dotClass: meta.dotClass,
        };
      });
    },
    currentProfileLabel() {
      const matchedProfile = this.profileOptions.find(
        (profile) => profile.value === this.selectedAM35Profile
      );
      return matchedProfile ? matchedProfile.label : "配置1";
    },
    isProfileSwitching() {
      return this.pendingProfileValue !== null;
    },
    am35ProfileKeySwitchEnabled: {
      get() {
        return Boolean(Number(this.mouseArgs?.AM35ProfileKeySwitch ?? 0));
      },
      set(value) {
        this.updateAM35ProfileKeySwitch(value ? 1 : 0);
      },
    },
    connectedWay() {
      const { code, MouseSn } = this.currentDevice;
      let connectedWay = "dongle";
      const codeImageMap = {
        am35: "mouse",
        am35_d: "dongle",
      };
      connectedWay = codeImageMap[code] || "dongle";
      return connectedWay;
    },
    /**
     * 获取当前按键的 combo 字符串
     * 格式：'1000' = Ctrl, '0100' = Shift, '0010' = Alt, '0001' = Win
     */
    currentCombo() {
      if (!this.activeKey || !this.keyMatrix[this.activeKey]) {
        return '0000';
      }
      const keyMapping = this.keyMatrix[this.activeKey];
      const keyType = keyMapping[0];
      
      // 只有键盘按键类型才有 combo
      if (keyType == 6 && keyMapping.length >= 4) {
        return keyMapping[3] || '0000';
      }
      return '0000';
    },
    
    // 组合键 Checkbox 绑定（多选模式）
    comboCtrl: {
      get() { return this.currentCombo[0] === '1'; },
      set(v) { this.updateCombo(0, v); }
    },
    comboShift: {
      get() { return this.currentCombo[1] === '1'; },
      set(v) { this.updateCombo(1, v); }
    },
    comboAlt: {
      get() { return this.currentCombo[2] === '1'; },
      set(v) { this.updateCombo(2, v); }
    },
    comboWin: {
      get() { return this.currentCombo[3] === '1'; },
      set(v) { this.updateCombo(3, v); }
    },
    
    /**
     * 获取组合键对应的虚拟键盘选中按键
     */
    comboVirtualKey() {
      if (!this.activeKey || !this.keyMatrix[this.activeKey]) {
        return null;
      }
      const keyMapping = this.keyMatrix[this.activeKey];
      const keyType = keyMapping[0];
      
      // 只有键盘按键类型才有虚拟键盘
      if (keyType == 6 && keyMapping.length >= 4) {
        return keyMapping[2] || null; // sdtKey
      }
      return null;
    },
    
    mouseBackground() {
      const { code, MouseSn } = this.currentDevice;
      console.log(code, MouseSn, "this.currentDevice");
      const snInfo = parseAM35SN(MouseSn);
      // 可扩展的 code -> imageName 映射表，未来新增设备只需在这里添加条目
      const codeImageMap = {
        am35: getAM35MianImageKey(snInfo),
        am35_d: getAM35MianImageKey(snInfo),
        // add more mappings here when new codes are introduced
      };

      // 默认图片 key（回退使用）
      const defaultImageKey = "device_am35_m_b";

      const imageName = codeImageMap[code] || defaultImageKey;
      const bgUrl = this.images?.[imageName] || this.images?.[defaultImageKey] || "none";

      const styleObj = {
        backgroundImage: `url(${bgUrl})`,
        backgroundPosition: "center",
        backgroundSize: "auto 100%",
        backgroundRepeat: "no-repeat",
      };

      return styleObj;
    },
  },
  methods: {
    // ==================== 步骤加载器场景配置 ====================
    /**
     * 定义步骤加载器的场景配置
     * @returns {Object} 场景配置对象
     */
    stepLoaderScenes() {
      return {
        // 重置场景
        reset: {
          steps: [
            this.$t("mouse.resetSteps.resetting"),
            this.$t("mouse.resetSteps.resetSuccess"),
            this.$t("mouse.resetSteps.initializing"),
            this.$t("mouse.resetSteps.complete"),
          ],
          timeout: 30000,
          onStart: () => {
            this.pauseAM35BatteryGuard("reset");
            deviceCallbackManager.subscribe("initAM35Data", this._onResetInitComplete);
            this.$store.dispatch("loader/resetAM35");
            // 启动初始化超时监控（重置后等待 initAM35Data）
            this.startInitTimeout();
          },
          onComplete: () => {
            this.type = "keymap";
          },
          onTimeout: () => {
            deviceCallbackManager.unsubscribe("initAM35Data", this._onResetInitComplete);
            this.$message.error(this.$t("mouse.resetSteps.fail"));
            this.resumeAM35BatteryGuard("reset");
            // 清除超时定时器
            this.clearInitTimeout();
          },
          simulate: {
            enabled: !this.isWeb,
            delays: [2000, 1000],
          },
        },
        // 导出场景
        // 导出场景（仅网页端使用）
        // Qt 模式下文件选择窗口会阻塞 webview 渲染，不使用 StepLoader
        export: {
          steps: [
            this.$t("mouse.exportSteps.exporting"),
            this.$t("mouse.exportSteps.selectPath"),
            // 动态步骤：根据导出结果显示不同文案
            () => {
              if (this.exportResult === 'cancelled') {
                return this.$t("mouse.exportSteps.cancelled");
              } else if (this.exportResult === 'success') {
                return this.$t("mouse.exportSteps.success");
              } else {
                return this.$t("mouse.exportSteps.complete");
              }
            },
          ],
          timeout: 20000,
          onStart: (options) => {
            this.pauseAM35BatteryGuard("export");
            // 重置导出结果状态
            this.exportResult = null;
            // 订阅回调
            deviceCallbackManager.subscribe("saveAM35json", this._onExportSaveResult);
            // 发送导出指令
            this.$store.dispatch("loader/saveAM35json", options.saveData);
            // 下发指令后跳到"选择导出路径"步骤
            setTimeout(() => {
              if (this.slLoading && this.slActiveScene === 'export') {
                this.setStepLoaderStep(1);
              }
            }, 200);
          },
          onComplete: () => {},
          onTimeout: () => {
            deviceCallbackManager.unsubscribe("saveAM35json", this._onExportSaveResult);
            this.resumeAM35BatteryGuard("export", { refresh: true, force: true });
            this.$message.error(this.$t("mouse.exportSteps.fail"));
          },
          simulate: {
            enabled: false,
          },
        },
        import: {
          steps: [
            this.$t("mouse.importSteps.selectFile"),
            this.$t("mouse.importSteps.importing"),
            () =>
              this.importResult === "fail"
                ? this.$t("mouse.importSteps.fail")
                : this.$t("mouse.importSteps.success"),
          ],
          timeout: 35000,
          onStart: () => {
            this.pauseAM35BatteryGuard("import");
            this.importResult = null;
            this.clearImportFlowTimeout();
            this.clearSceneFallbackTimer("importCancelFallbackTimer");
            this.startImportFlowTimeout();
            setTimeout(() => {
              this.$store.dispatch("loader/loadAM35json");
            }, 100);
          },
          onComplete: () => {
            this.clearImportFlowTimeout();
            this.clearSceneFallbackTimer("importCancelFallbackTimer");
            this.type = "keymap";
            this.resumeAM35BatteryGuard("import", { refresh: true, force: true });
          },
          onTimeout: () => {
            this.clearImportFlowTimeout();
            this.clearSceneFallbackTimer("importCancelFallbackTimer");
            this.resumeAM35BatteryGuard("import", { refresh: true, force: true });
            this.$message.error(this.$t("mouse.importSteps.fail"));
          },
          simulate: {
            enabled: false,
          },
        },
        switchProfile: {
          steps: ["切换配置中", "正在初始化", "切换配置成功"],
          timeout: 30000,
          onStart: (options) => {
            const profileValue = Number(options?.profileValue);
            if (Number.isNaN(profileValue)) {
              console.warn("[AM35] 无效的配置编号", options?.profileValue);
              this.closeStepLoader();
              return;
            }

            this.pauseAM35BatteryGuard("switchProfile");
            this.pendingProfileValue = profileValue;
            deviceCallbackManager.subscribe("initAM35Data", this._onSwitchProfileInitComplete);
            this.$store.dispatch("loader/setAM35Profile", {
              setAM35Profile: profileValue,
            });
            // 宿主端暂时不会上报 setAM35Profile，这里直接复用重置的分步展示，
            // 最终仍以 initAM35Data 作为切换完成信号。
            setTimeout(() => {
              if (this.slLoading && this.slActiveScene === "switchProfile") {
                this.setStepLoaderStep(1);
                this.startInitTimeout();
              }
            }, 1200);
          },
          onComplete: () => {
            this.pendingProfileValue = null;
            this.type = "keymap";
          },
          onTimeout: () => {
            deviceCallbackManager.unsubscribe("initAM35Data", this._onSwitchProfileInitComplete);
            this.pendingProfileValue = null;
            this.resumeAM35BatteryGuard("switchProfile", { refresh: true, force: true });
            this.clearInitTimeout();
            this.$message.error("切换配置失败");
          },
          simulate: {
            enabled: false,
          },
        },
      };
    },

    /**
     * 重置完成回调（收到 initAM35Data）
     */
    _onResetInitComplete() {
      deviceCallbackManager.unsubscribe("initAM35Data", this._onResetInitComplete);
      // 清除超时定时器（已成功收到回调）
      this.clearInitTimeout();
      this.resumeAM35BatteryGuard("reset", { refresh: true, force: true });
      this.completeStepLoader("reset");
    },
    _onSwitchProfileInitComplete() {
      if (!this.slLoading || this.slActiveScene !== "switchProfile") return;

      deviceCallbackManager.unsubscribe("initAM35Data", this._onSwitchProfileInitComplete);
      this.clearInitTimeout();
      this.resumeAM35BatteryGuard("switchProfile", { refresh: true, force: true });
      this.completeStepLoader("switchProfile");
    },

    /**
     * 网页端导出结果回调（收到 saveAM35json）
     * 回调格式: {"method": "saveAM35json", "data": "cancelled"} 或 "success"
     */
    _onExportSaveResult(json) {
      console.log("[Export] saveAM35json callback:", json);
      if (!this.slLoading || this.slActiveScene !== 'export') {
        console.log("[Export] Ignored - not in export scene");
        return;
      }
      try {
        // 支持多种数据格式
        let result = json;
        if (typeof json === 'string') {
          try {
            const parsed = JSON.parse(json);
            result = parsed.data || parsed;
          } catch {
            result = json;
          }
        } else if (json && typeof json === 'object') {
          result = json.data || json;
        }
        
        console.log("[Export] Parsed result:", result);
        
        if (result === "cancelled" || result === "success") {
          this.exportResult = result;
          deviceCallbackManager.unsubscribe("saveAM35json", this._onExportSaveResult);
          this.resumeAM35BatteryGuard("export", { refresh: true, force: true });
          this.completeStepLoader("export");
        }
      } catch (e) {
        console.error("[Export] Parse error:", e);
      }
    },

    /**
     * Web模式：处理 resetAM35 上报
     */
    onHostResetAM35(json) {
      if (!json || !this.stepLoaderVisible) return;
      try {
        const data = JSON.parse(json);
        if (data === "success") {
          this.setStepLoaderStep(1); // 重置成功
          setTimeout(() => {
            if (this.stepLoaderVisible) {
              this.setStepLoaderStep(2); // 初始化中
              // 重置成功后，启动初始化超时监控
              this.startInitTimeout();
            }
          }, 1200);
        }
      } catch (e) {
        console.error("[Reset] Parse error:", e);
      }
    },

    // ==================== 初始化超时管理 ====================
    
    /**
     * 启动初始化数据超时监控
     * 10 秒内未收到 initAM35Data 回调时，主动补发一次电量请求，
     * 让电量组件继续等待宿主后续状态上报。
     */
    startInitTimeout() {
      this.clearInitTimeout();
      this.initDataTimer = setTimeout(() => {
        console.warn("initAM35Data 超时（10秒），补发一次电量请求");
        this.requestAM35Battery();
      }, 10000);
      console.log("已启动 initAM35Data 超时监控（10秒）");
    },

    requestAM35Battery() {
      const electricity = this.$refs.am35Electricity;
      if (!electricity || typeof electricity.requestBattery !== "function") {
        console.warn("AM35 电量组件未就绪，无法主动获取电量");
        return;
      }
      electricity.requestBattery({ force: true });
    },
    pauseAM35BatteryGuard(source) {
      const electricity = this.$refs.am35Electricity;
      if (!electricity || typeof electricity.pauseBatteryGuard !== "function") {
        console.warn(`AM35 电量组件未就绪，无法暂停电量守卫: ${source}`);
        return;
      }
      electricity.pauseBatteryGuard(source);
    },
    resumeAM35BatteryGuard(source, options = {}) {
      const electricity = this.$refs.am35Electricity;
      if (!electricity || typeof electricity.resumeBatteryGuard !== "function") {
        console.warn(`AM35 电量组件未就绪，无法恢复电量守卫: ${source}`);
        return;
      }
      electricity.resumeBatteryGuard(source, options);
    },
    isImportStepLoaderActive() {
      return this.slLoading && this.slActiveScene === "import";
    },
    startImportFlowTimeout() {
      this.clearImportFlowTimeout();
      this.importFlowTimer = setTimeout(() => {
        if (!this.isImportStepLoaderActive()) return;

        this.importResult = "fail";
        this.setStepLoaderStep(2);
        this.importFlowCloseTimer = setTimeout(() => {
          if (!this.isImportStepLoaderActive()) return;

          this.closeStepLoader();
          this.resumeAM35BatteryGuard("import", { refresh: true, force: true });
          this.$message.error(this.$t("mouse.importSteps.fail"));
        }, 800);
      }, 30000);
    },
    clearImportFlowTimeout() {
      if (this.importFlowTimer) {
        clearTimeout(this.importFlowTimer);
        this.importFlowTimer = null;
      }
      if (this.importFlowCloseTimer) {
        clearTimeout(this.importFlowCloseTimer);
        this.importFlowCloseTimer = null;
      }
    },
    /**
     * 清除初始化数据超时定时器
     */
    clearInitTimeout() {
      if (this.initDataTimer) {
        clearTimeout(this.initDataTimer);
        this.initDataTimer = null;
        console.log("已清除 initAM35Data 超时监控");
      }
    },

    // ==================== 按键描述和状态相关方法 ====================
    
    /**
     * 获取虚拟键盘组件应该高亮的按键
     * 新格式：[6, consumerKey, sdtKey, combo]
     */
    getVirtualKeyboardSelectedKey() {
      if (!this.activeKey || !this.keyMatrix[this.activeKey]) {
        return null;
      }
      
      const keyMapping = this.keyMatrix[this.activeKey];
      const keyType = keyMapping[0];
      
      if (keyType != 6 && keyType != '6') {
        return null;
      }
      
      // 新格式：[6, consumerKey, sdtKey, combo]
      // 如果选择了组合键（combo不为0000），虚拟键盘不选中任何键
      const combo = keyMapping[3] || '0000';
      if (combo !== '0000') {
        return null;
      }
      
      // 虚拟键盘只关心 sdtKey（标准按键）
      return keyMapping[2] || null;
    },
    
    // 获取按键的显示描述
    keyDesc(keyId) {
      const keyMapping = this.keyMatrix[keyId];
      if (!keyMapping || keyMapping.length === 0) {
        return "";
      }

      const keyType = keyMapping[0];

      // 火力按键模式
      if (keyType == "0x0b") {
        return this.getFirepowerKeyDesc(keyMapping);
      }

      // 重映射为键盘按键
      // 新格式：[6, consumerKey, sdtKey, combo]
      if (keyType == "6" && keyMapping.length > 1) {
        const consumerKey = keyMapping[1];
        const sdtKey = keyMapping[2];
        const combo = keyMapping[3] || '0000';
        
        // 判断按键类型：consumerKey (功能键) 或 sdtKey (标准按键)
        const keyCode = sdtKey || consumerKey;
        
        // 如果有组合键，显示组合键+按键
        if (combo && combo !== '0000') {
          return this.getComboKeyDesc(combo, keyCode);
        }
        
        return this.getKeyboardKeyDesc(keyCode);
      }

      // 宏按键 - 格式: [8, repeat_mode, repeat_times, macro_name, keyOrder]
      if (keyType == "8" || keyType == 8) {
        return this.getMacroKeyDesc(keyMapping);
      }

      // 原生鼠标按键
      return this.getMouseKeyDesc(keyMapping);
    },

    // 判断按键是否被禁用
    keyDisabled(keyId) {
      const keyMapping = this.keyMatrix[keyId];
      if (!keyMapping) return false;

      // 检查是否是左键映射且是最后一个左键
      const isLeftClickMapping = keyMapping.toString() === [1].toString();
      if (!isLeftClickMapping) return false;

      // 统计有多少个按键映射为左键
      const leftClickCount = Object.values(this.keyMatrix).filter(
        (mapping) => mapping && mapping.toString() === [1].toString()
      ).length;

      // 如果只剩一个左键映射，则禁用（防止用户失去左键功能）
      return leftClickCount <= 1;
    },

    // 获取火力按键描述
    getFirepowerKeyDesc(keyMapping) {
      const allKeys = VirtualKeyboard.allKeys;
      const keyText = allKeys[keyMapping[1]]?.text?.replace("<br/>", "") || "";
      return this.$t("mouse.firepowerKey") + " [" + keyText + "]";
    },

    // 判断基础按键是否被选中（大小写不敏感）
    isKeySelected(keyValue) {
      if (!this.selectedKey || !keyValue) return false;
      // 大小写不敏感比较
      return this.selectedKey.toLowerCase() === keyValue.toLowerCase();
    },

    // 获取键盘按键描述
    getKeyboardKeyDesc(keyCode) {
      if (!keyCode) return '';
      
      // 优先从虚拟键盘获取（尝试原值和小写）
      if (VKB.allKeys[keyCode]) {
        return VKB.allKeys[keyCode].text.replace("<br/>", "");
      }
      const lowerKeyCode = keyCode.toLowerCase();
      if (VKB.allKeys[lowerKeyCode]) {
        return VKB.allKeys[lowerKeyCode].text.replace("<br/>", "");
      }

      // 其次从HID键值获取翻译（尝试原值和大写）
      if (this.$te(`hidKeys.${keyCode}`)) {
        return this.$t(`hidKeys.${keyCode}`);
      }
      const upperKeyCode = keyCode.toUpperCase().replace('0X', '0x');
      if (upperKeyCode !== keyCode && this.$te(`hidKeys.${upperKeyCode}`)) {
        return this.$t(`hidKeys.${upperKeyCode}`);
      }

      // 最后返回原始键码
      return keyCode;
    },
    
    // 获取组合键描述
    // combo 格式：'1000'=Ctrl, '0100'=Shift, '0010'=Alt, '0001'=Win
    getComboKeyDesc(combo, keyCode) {
      const modifiers = [];
      
      if (combo[0] === '1') modifiers.push('Ctrl');
      if (combo[1] === '1') modifiers.push('Shift');
      if (combo[2] === '1') modifiers.push('Alt');
      if (combo[3] === '1') modifiers.push('Win');
      
      const keyName = this.getKeyboardKeyDesc(keyCode);
      
      return modifiers.join('+') + '+' + keyName;
    },

    // 获取宏按键描述
    // 格式: keyMapping = [8, repeat_mode, repeat_times, macro_name, keyOrder数组]
    getMacroKeyDesc(keyMapping) {
      const macroName = keyMapping[3] || "";
      return this.$t("mouse.macro") + " " + macroName;
    },

    // 获取鼠标按键描述
    getMouseKeyDesc(keyMapping) {
      // 在所有按键中查找匹配的按键定义
      const targetKey = this.allKeys.find(
        (keyDef) => keyDef.key.toString() === keyMapping.toString()
      );

      if (targetKey) {
        return this.$t("mouse.macroDesc." + targetKey.desc);
      }

      return "";
    },

    getBasicKeyLabel(key) {
      const hidKey = `hidKeys.${key.key[1]}`;
      const macroDescKey = `mouse.macroDesc.${key.desc}`;

      if (this.$te(hidKey)) return this.$t(hidKey);
      if (this.$te(macroDescKey)) return this.$t(macroDescKey);

      return key.desc;
    },

    // ==================== 按键点击和选择事件处理 ====================

    syncSelectionFromActiveKey(key = this.activeKey, options = {}) {
      const { allowDisabled = false } = options;
      if (!allowDisabled && this.keyDisabled(key)) return;

      const keyMapping = this.keyMatrix?.[key];
      if (!keyMapping || keyMapping.length === 0) return;

      this.activeKey = key;
      const keyType = keyMapping[0];

      if ((keyType === "6" || keyType === 6) && keyMapping.length > 1) {
        // 重映射为键盘按键
        // 新格式：[6, consumerKey, sdtKey, combo]
        const consumerKey = keyMapping[1];
        const sdtKey = keyMapping[2];
        const keyCode = consumerKey || sdtKey;
        
        // keyCode 已经是标准化的大写格式，直接使用
        this.selectedKey = `6,${keyCode}`;
        this.selectedMacroName = "";
        // 切换到快捷键 tab
        this.hotkeyOrMacro = 'hotkey';
      } else if (keyType === "8" || keyType === 8) {
        // 宏按键 - 格式: [8, repeat_mode, repeat_times, macro_name, keyOrder数组]
        this.selectedKey = "8";
        this.selectedMacroName = keyMapping[3] || "";
        this.updateMacroRepeatSettings(this.selectedMacroName);
        // 切换到宏 tab
        this.hotkeyOrMacro = 'macro';
      } else {
        // 原生鼠标按键
        this.selectedKey = keyMapping.toString();
        this.selectedMacroName = "";
      }
    },

    refreshKeymapSelection(options = {}) {
      if (this.type !== "keymap" || !this.activeKey) {
        return;
      }

      const keyMapping = this.keyMatrix?.[this.activeKey];
      if (!keyMapping || keyMapping.length === 0) {
        return;
      }

      const keyType = keyMapping[0];
      const isMacroBinding = keyType === "8" || keyType === 8;
      const shouldRefresh =
        options.force === true ||
        this.selectedKey === "8" ||
        !!this.selectedMacroName ||
        isMacroBinding;

      if (!shouldRefresh) {
        return;
      }

      this.syncSelectionFromActiveKey(this.activeKey, {
        allowDisabled: true,
      });
    },

    onMouseClick(key) {
      this.syncSelectionFromActiveKey(key);
    },

    /**
     * 独立请求组合键切换 Profile 开关，补齐 initAM35Data 之外的参数链路。
     */
    requestAM35ProfileKeySwitch() {
      this.$store.dispatch("loader/getAM35ProfileKeySwitch");
    },

    /**
     * 更新组合键切换 Profile 开关并同步到设备。
     */
    updateAM35ProfileKeySwitch(nextValue) {
      const payload = {
        AM35ProfileKeySwitch: Number(nextValue ? 1 : 0),
      };
      this.$store.dispatch("mouseAM35/setAM35ProfileKeySwitch", payload);
      this.$store.dispatch("loader/setAM35ProfileKeySwitch", payload);
    },

    /**
     * 处理按键选择事件
     * 当用户从鼠标按键或基础按键列表中选择一个按键时触发
     */
    onSelectedKey(e) {
      if (!this.activeKey) return;

      const selectedValue = e.target.value;
      this.updateKeyMapping(selectedValue);
    },

    /**
     * 更新按键映射配置
     * @param {string} selectedValue - 选中的按键值（可能是单个值或逗号分隔的值）
     */
    updateKeyMapping(selectedValue) {
      // 更新本地状态
      this.selectedKey = selectedValue;
      this.selectedMacroName = "";

      // 更新store中的按键矩阵
      const newKeyMapping = selectedValue.split(",");
      this.updateStoreKeyMatrix(this.activeKey, newKeyMapping);

      // 同步按键配置到设备
      this.syncKeyConfigurationToDevice();
    },

    /**
     * 更新Store中的按键矩阵数据
     * @param {string} keyId - 按键ID
     * @param {Array} keyMapping - 新的按键映射数组
     */
    updateStoreKeyMatrix(keyId, keyMapping) {
      const keyMatrix = { ...this.keyMatrix };
      keyMatrix[keyId] = keyMapping;
      // 调试日志：打印Store更新的内容
      console.log(`🔄 Store更新 - ${keyId}:`, keyMapping);
      this.$store.dispatch("mouseAM35/setKeyMatrix", { ...keyMatrix });
    },

    /**
     * 同步按键配置到设备
     * 将当前的按键配置转换为设备协议格式并发送
     */
    syncKeyConfigurationToDevice() {
      const deviceProtocol = this.buildDeviceProtocolForKeyConfiguration();
      this.$store.dispatch("loader/changeAM35MouseKey", deviceProtocol);
    },

    /**
     * 构建按键配置的设备协议数据
     * @returns {Object} 设备协议格式的按键配置对象
     *
     * ============================================================================
     * AM35 按键映射格式说明 (KeyMatrix)
     * ============================================================================
     * 
     * 【1. 原生鼠标按键】
     * 格式: [toKeyID]
     * 示例: [1], [2], [3], [4], [5], [12], [13]
     * 说明: 
     *   - 1=左键, 2=右键, 3=滚轮键, 4=前进, 5=后退, 12=滚轮上, 13=滚轮下
     *   - selectedKey = "1", "2", "3", ...
     * 
     * 【2. 键盘按键映射】
     * 格式: [6, consumerKey, sdtKey, combo]
     * 说明:
     *   - toKeyID = 6 表示键盘按键类型
     *   - consumerKey: 功能按键码（如 0x0192=计算器, 0x00CD=播放/暂停）
     *   - sdtKey: 标准HID按键码（如 0x04=A, 0x1E=1）
     *   - combo: 组合键修饰符（'1000'=Ctrl, '0100'=Shift, '0010'=Alt, '0001'=Win, '0000'=无）
     *   - consumerKey 和 sdtKey 互斥，一个有值另一个为空字符串
     * 
     * 示例:
     *   - 标准按键 A:        [6, '', '0x04', '0000']
     *   - 功能按键 计算器:   [6, '0x0192', '', '0000']
     *   - 组合键 Ctrl+A:     [6, '', '0x04', '1000']
     *   - 组合键 Ctrl+Shift: [6, '', '0x04', '1100']
     *   - selectedKey = "6,0x04" 或 "6,0x0192"
     * 
     * 【3. 宏按键映射】
     * 格式: [8, repeat_mode, repeat_times, macro_name, keyOrder数组]
     * 说明:
     *   - toKeyID = 8 表示宏按键类型
     *   - repeat_mode: 0=重复N次, 1=按住触发, 2=自动循环
     *   - repeat_times: 重复次数（仅当 repeat_mode=0 时有效）
     *   - macro_name: 宏名称
     *   - keyOrder: 宏按键序列数组
     *   - selectedKey = "8"
     * 
     * 示例: [8, 0, 5, "MyMacro", [...keyOrder]]
     * 
     * 【4. 禁用按键】
     * 格式: [0] 或相关禁用标识
     * 说明: selectedKey = "0"
     * 
     * 【5. 火力按键】(已禁用)
     * 格式: ['0x0b', keyCode, interval, repeatCount]
     * 说明: 快速连点模式，当前 AM35 不支持
     * 
     * ============================================================================
     * 设备协议下发格式
     * ============================================================================
     * 
     * 原生鼠标按键:
     *   { keyID, toKeyID, payload: [], macro_name: "", repeat_mode: "", repeat_times: "" }
     * 
     * 键盘按键:
     *   { keyID, toKeyID: 6, consumerKey, sdtKey, combo }
     * 
     * 宏按键:
     *   { keyID, toKeyID: 8, macro_name, repeat_mode, repeat_times, payload: keyOrder }
     * ============================================================================
     */
    buildDeviceProtocolForKeyConfiguration() {
      const keyID = this.extractKeyID(this.activeKey);
      const toKeyID = this.extractTargetKeyID();

      const baseProtocol = {
        keyID,
        toKeyID,
        payload: [],
        macro_name: "",
        repeat_mode: "",
        repeat_times: "",
        setAM35MacroLoopInterval: {},
      };

      // 根据按键类型设置不同的协议配置
      if (this.isKeyboardKeyMapping(toKeyID)) {
        return this.buildKeyboardKeyProtocol(baseProtocol);
      } else if (this.isMacroKeyMapping(toKeyID)) {
        return this.buildMacroKeyProtocol(baseProtocol);
      } else if (this.isDpiStageKeyMapping(toKeyID)) {
        return this.buildDpiStageKeyProtocol(baseProtocol);
      } else if (this.isProfileToggleKeyMapping(toKeyID)) {
        return this.buildProfileToggleKeyProtocol(baseProtocol);
      } else {
        return baseProtocol; // 原生鼠标按键或其他类型
      }
    },

    /**
     * 提取按键ID（去除0x0前缀）
     */
    extractKeyID(activeKey) {
      return activeKey.replace("0x0", "");
    },

    /**
     * 提取目标按键ID
     */
    extractTargetKeyID() {
      return this.selectedKey.split(",")[0];
    },

    /**
     * 判断是否为键盘按键映射
     * 键盘按键映射格式：selectedKey = "6,按键码"（有逗号分隔）
     * 原生鼠标按键6格式：selectedKey = "6"（无逗号分隔）
     */
    isKeyboardKeyMapping(toKeyID) {
      // 保持与原始逻辑一致：使用 == 比较，支持数字和字符串
      return toKeyID == 6 && this.selectedKey.includes(",");
    },

    /**
     * 判断是否为宏按键映射
     */
    isMacroKeyMapping(toKeyID) {
      // 保持与原始逻辑一致：使用 == 比较，支持数字和字符串
      return toKeyID == "8";
    },

    /**
     * AM35 Type 11: DPI 档位控制，selectedKey = "11,dpiAction,dpiTarget"。
     */
    isDpiStageKeyMapping(toKeyID) {
      return toKeyID == "11";
    },

    buildDpiStageKeyProtocol(baseProtocol) {
      const [, dpiAction = 0, dpiTarget = 0] = this.selectedKey.split(",");
      return {
        keyID: baseProtocol.keyID,
        toKeyID: baseProtocol.toKeyID,
        dpiAction: Number(dpiAction),
        dpiTarget: Number(dpiTarget),
      };
    },

    isProfileToggleKeyMapping(toKeyID) {
      return toKeyID == "14";
    },

    buildProfileToggleKeyProtocol(baseProtocol) {
      return {
        keyID: baseProtocol.keyID,
        toKeyID: 14,
        macro_name: "",
        repeat_mode: "",
        repeat_times: "",
        consumerKey: "",
        sdtKey: "",
        combo: "",
        payload: [],
      };
    },

    /**
     * 构建键盘按键的设备协议配置
     * 
     * 新协议格式说明：
     * - sdtKey: 虚拟键盘选择的标准按键（如 0x04 代表 A 键）
     * - consumerKey: 基础功能按键（如 0x0192 代表打开计算器）
     * - sdtKey 和 consumerKey 互斥，一个有值另一个为空字符串
     * - combo: 组合键字段（'1000'=Ctrl, '0100'=Shift, '0010'=Alt, '0001'=Win, '0000'=无）
     * 
     * 判断规则：
     * - 如果按键码长度 <= 4（如 0x04, 0x1E），使用 sdtKey
     * - 如果按键码长度 > 4（如 0x0192, 0x0183），使用 consumerKey
     * 
     * @param {Object} baseProtocol - 基础协议对象
     * @returns {Object} 键盘按键协议对象
     */
    buildKeyboardKeyProtocol(baseProtocol) {
      // 从 store 中获取完整的按键映射信息
      const keyMapping = this.keyMatrix[this.activeKey];
      
      if (!keyMapping || keyMapping.length < 4) {
        // 如果数据不完整，使用旧逻辑
        const keyboardKeyCode = this.selectedKey.split(",")[1];
        const isStandardKey = keyboardKeyCode.length <= 4;
        
        return {
          keyID: baseProtocol.keyID,
          toKeyID: baseProtocol.toKeyID,
          consumerKey: isStandardKey ? '' : keyboardKeyCode,
          sdtKey: isStandardKey ? keyboardKeyCode : '',
          combo: '0000',
        };
      }
      
      // 新格式：[6, consumerKey, sdtKey, combo]
      const consumerKey = keyMapping[1] || '';
      const sdtKey = keyMapping[2] || '';
      const combo = keyMapping[3] || '0000';

      // 新协议结构
      return {
        keyID: baseProtocol.keyID,
        toKeyID: baseProtocol.toKeyID,
        consumerKey: consumerKey,
        sdtKey: sdtKey,
        combo: combo,
      };
    },

    /**
     * 构建宏按键的设备协议配置
     * keyMatrix 格式: [8, repeat_mode, repeat_times, macro_name, keyOrder数组]
     * 
     * 注意: 索引[3]是 macro_name，索引[1]是 repeat_mode
     * 修复记录: 2025-10-31 - 修正了从索引[1]读取宏名称的错误
     */
    buildMacroKeyProtocol(baseProtocol) {
      if (!this.mouseMacroData || this.mouseMacroData.length === 0) {
        return baseProtocol;
      }

      // 从 keyMatrix 中获取宏名称 - 索引[3]是macro_name
      const macroName = this.keyMatrix[this.activeKey][3];
      const macro = this.findMacroByName(macroName);

      if (!macro) {
        console.warn("未找到匹配的宏: " + macroName);
        this.$message.error(`未找到匹配的宏: ${macroName}`);
        return baseProtocol;
      }

      // 更新Store中的宏数据
      this.updateStoreMacroConfiguration(macro, baseProtocol.keyID, baseProtocol.toKeyID);

      return {
        ...baseProtocol,
        macro_name: macroName,
        repeat_mode: this.repeatMode,
        repeat_times: this.repeatTimes,
        payload: macro.macroValue?.keyOrder || [],
        setAM35MacroLoopInterval: this.resolveMacroLoopIntervalPayload(macro),
      };
    },

    /**
     * 根据名称查找宏
     */
    findMacroByName(macroName) {
      return this.mouseMacroData.find((macro) => macro.macro_name === macroName);
    },
    findUiMacroByName(macroName) {
      const macros =
        this.macrosForBinding ||
        (this.macroData?.length ? this.macroData : this.mouseMacroData) ||
        [];
      return macros.find(
        (macro) => macro.macro_name === macroName
      );
    },
    getMacroOptionLabel(macro) {
      if (macro?.macro_type === "click") {
        return `${macro.macro_name}（${this.$t("mouse.clickMacro")}）`;
      }
      return macro?.macro_name || "";
    },
    resolveMacroLoopIntervalPayload(macro) {
      const intervalValue =
        macro?.macro_name &&
        this.selectedMacroName &&
        macro.macro_name === this.selectedMacroName
          ? this.macroLoopInterval
          : macro?.setAM35MacroLoopInterval?.interval;
      return {
        interval: this.normalizeMacroLoopIntervalValue(intervalValue, 50),
      };
    },

    /**
     * 更新Store中的宏配置数据
     */
    updateStoreMacroConfiguration(macro, keyID, toKeyID) {
      const mouseMacroData = [...this.mouseMacroData];
      const macroIndex = mouseMacroData.findIndex((item) => item.macro_name === macro.macro_name);

      if (macroIndex !== -1) {
        mouseMacroData[macroIndex].keyID = keyID;
        mouseMacroData[macroIndex].toKeyID = toKeyID;
        mouseMacroData[macroIndex].repeat_mode = this.repeatMode;
        mouseMacroData[macroIndex].repeat_times = this.repeatTimes;
        mouseMacroData[macroIndex].setAM35MacroLoopInterval =
          this.resolveMacroLoopIntervalPayload(macro);

        this.$store.dispatch("mouseAM35/setMouseMacroData", mouseMacroData);
      }
    },
    
    // ==================== 组合键相关方法 ====================
    /**
     * 设置单选组合键
     * @param {string} combo - 要设置的组合键（'1000'=Ctrl, '0100'=Shift, '0010'=Alt, '0001'=Win）
     */
    setComboSingle(combo) {
      if (!this.activeKey) {
        return;
      }
      
      const keyMapping = this.keyMatrix[this.activeKey];
      if (!keyMapping) {
        return;
      }
      
      const keyType = keyMapping[0];
      
      // 如果不是键盘按键类型，初始化为键盘按键格式
      if (keyType != 6 || keyMapping.length < 4) {
        const newMapping = [
          6,              // toKeyID
          '',             // consumerKey
          '0x04',         // sdtKey（默认为 A 键）
          combo          // combo
        ];
        
        this.updateStoreKeyMatrix(this.activeKey, newMapping);
        
        // 更新本地状态
        this.selectedKey = '6,0x04';
        this.selectedMacroName = "";
        
        // 同步到设备
        this.syncKeyConfigurationToDevice();
        
        return;
      }
      
      // 更新 store
      const newMapping = [
        keyMapping[0],  // toKeyID
        keyMapping[1],  // consumerKey
        keyMapping[2] || '0x04',  // sdtKey（如果为空，默认 A 键）
        combo          // combo
      ];
      
      this.updateStoreKeyMatrix(this.activeKey, newMapping);
      
      // 同步到设备
      this.syncKeyConfigurationToDevice();
    },
    
    /**
     * 更新 combo 字符串中的某一位
     * @param {number} position - 位置索引 (0=Ctrl, 1=Shift, 2=Alt, 3=Win)
     * @param {boolean} value - 是否选中
     */
    updateCombo(position, value) {
      if (!this.activeKey) {
        return;
      }
      
      const keyMapping = this.keyMatrix[this.activeKey];
      if (!keyMapping) {
        return;
      }
      
      const keyType = keyMapping[0];
      
      // 如果不是键盘按键类型，初始化为键盘按键格式
      if (keyType != 6 || keyMapping.length < 4) {
        // 初始化为键盘按键格式：[6, '', 'A键', combo]
        let combo = '0000'.split('');
        combo[position] = value ? '1' : '0';
        
        const newMapping = [
          6,              // toKeyID
          '',             // consumerKey
          '0x04',         // sdtKey（默认为 A 键）
          combo.join('') // combo
        ];
        
        this.updateStoreKeyMatrix(this.activeKey, newMapping);
        
        // 更新本地状态
        this.selectedKey = '6,0x04';
        this.selectedMacroName = "";
        
        // 同步到设备
        this.syncKeyConfigurationToDevice();
        
        return;
      }
      
      // 获取当前 combo
      let combo = (keyMapping[3] || '0000').split('');
      
      // 更新指定位置
      combo[position] = value ? '1' : '0';
      
      // 更新 store
      const newMapping = [
        keyMapping[0],  // toKeyID
        keyMapping[1],  // consumerKey
        keyMapping[2] || '0x04',  // sdtKey（如果为空，默认 A 键）
        combo.join('') // combo
      ];
      
      this.updateStoreKeyMatrix(this.activeKey, newMapping);
      
      // 同步到设备
      this.syncKeyConfigurationToDevice();
    },
    
    /**
     * 处理组合键虚拟键盘选择
     * @param {string} keyCode - 选中的按键码
     */
    onComboVirtualKeyboardChange(keyCode) {
      if (!this.activeKey || !this.keyMatrix[this.activeKey]) {
        return;
      }
      
      const keyMapping = this.keyMatrix[this.activeKey];
      const keyType = keyMapping[0];
      
      // HID标准组合键码映射表（左右键统一）
      const comboMap = {
        "0xe0": "1000", // Left Ctrl
        "0xe4": "1000", // Right Ctrl
        "0xe1": "0100", // Left Shift
        "0xe5": "0100", // Right Shift
        "0xe2": "0010", // Left Alt
        "0xe6": "0010", // Right Alt
        "0xe3": "0001", // Left Win
        "0xe7": "0001", // Right Win
      };
      
      const lowerKeyCode = keyCode.toLowerCase();
      const currentCombo = keyMapping[3] || '0000';
      
      // 特殊处理：当前combo为0000且选择的是组合键码，则下发combo+A键
      if (currentCombo === '0000' && comboMap[lowerKeyCode]) {
        const newMapping = [
          6,                      // toKeyID
          '',                     // consumerKey
          '0x04',                 // sdtKey (A键)
          comboMap[lowerKeyCode]  // combo
        ];
        this.updateStoreKeyMatrix(this.activeKey, newMapping);
        this.syncKeyConfigurationToDevice();
        return;
      }
      
      // 只有键盘按键类型才能设置组合键
      if (keyType != 6 || keyMapping.length < 4) {
        // 如果不是键盘按键类型，初始化为键盘按键
        const newMapping = [
          6,           // toKeyID
          '',          // consumerKey
          keyCode,     // sdtKey
          '0000'       // combo
        ];
        this.updateStoreKeyMatrix(this.activeKey, newMapping);
        this.syncKeyConfigurationToDevice();
        return;
      }
      
      // 更新 sdtKey
      const newMapping = [
        keyMapping[0],  // toKeyID
        '',             // consumerKey（组合键只用 sdtKey）
        keyCode,        // sdtKey
        keyMapping[3]   // combo（保持不变）
      ];
      
      this.updateStoreKeyMatrix(this.activeKey, newMapping);
      this.syncKeyConfigurationToDevice();
    },
    
    /**
     * 处理键盘按键选择事件
     * 当用户从虚拟键盘中选择一个按键时触发
     */
    onSelectedKeyboard(value) {
      if (!this.activeKey) return;
      
      const keyboardKeyCode = value ?? "0x00";
      this.updateKeyboardKeyMapping(keyboardKeyCode);
    },

    /**
     * 更新键盘按键映射配置
     * @param {string} keyboardKeyCode - 键盘按键代码
     * 
     * 新的存储格式：[6, consumerKey, sdtKey, combo]
     * - 标准按键：[6, '', '0x04', '0000']
     * - 功能按键：[6, '0x0192', '', '0000']
     */
    updateKeyboardKeyMapping(keyboardKeyCode) {
      // 更新本地状态
      this.selectedKey = `6,${keyboardKeyCode}`;
      this.selectedMacroName = "";

      // 判断是标准按键还是功能按键
      const isStandardKey = keyboardKeyCode.length <= 4;
      
      // 构建新的键盘按键映射：[6, consumerKey, sdtKey, combo]
      const keyboardMapping = [
        6,                                    // toKeyID
        isStandardKey ? '' : keyboardKeyCode, // consumerKey
        isStandardKey ? keyboardKeyCode : '', // sdtKey
        '0000'                                // combo
      ];
      
      this.updateStoreKeyMatrix(this.activeKey, keyboardMapping);

      // 同步配置到设备
      this.syncKeyConfigurationToDevice();
    },
    /**
     * 处理火力按键配置
     * 火力按键是一种特殊的按键模式，可以设置间隔和重复次数
     * @param {number} index - 配置项索引 (1:按键, 2:间隔, 3:重复次数)
     * @param {string} value - 配置值
     */
    onSelectedFirepower(index, value) {
      if (!this.activeKey) return;

      // 更新本地状态
      this.selectedKey = "0x00";
      this.selectedMacroName = "";

      // 构建或更新火力按键配置
      this.updateStoreFirepowerKeyMapping(index, value);

      // 同步配置到设备
      this.syncKeyConfigurationToDevice();
    },

    /**
     * 更新火力按键映射配置
     * @param {number} index - 配置项索引
     * @param {string} value - 配置值
     */
    updateStoreFirepowerKeyMapping(index, value) {
      const keyMatrix = { ...this.keyMatrix };

      // 如果不是火力按键模式，初始化为默认配置
      if (keyMatrix[this.activeKey][0] !== "0x0b") {
        keyMatrix[this.activeKey] = ["0x0b", "0x04", "0x01", "0x01"];
      }

      // 确保值是十六进制格式
      let formattedValue = value;
      if (!value.includes("0x")) {
        formattedValue = `0x${Number(value).toString(16).padStart(2, "0")}`;
      }

      keyMatrix[this.activeKey][index] = formattedValue;
      this.$store.dispatch("mouseAM35/setKeyMatrix", { ...keyMatrix });
    },

    /**
     * 处理组合键配置
     * 组合键允许用户设置Ctrl、Shift、Alt等修饰键组合
     * @param {number} index - 修饰键位置索引
     * @param {string} value - 修饰键值
     */
    onSelectedHotkey(index, value) {
      if (!this.activeKey) return;

      // 更新本地状态
      this.selectedKey = "0x00";
      this.selectedMacroName = "";

      // 构建或更新组合键配置
      this.updateStoreHotkeyMapping(index, value);

      // 同步配置到设备
      this.syncKeyConfigurationToDevice();
    },

    /**
     * 更新组合键映射配置
     * @param {number} index - 修饰键位置索引
     * @param {string} value - 修饰键值
     */
    updateStoreHotkeyMapping(index, value) {
      const keyMatrix = { ...this.keyMatrix };

      // 如果不是组合键模式，初始化为默认配置
      if (keyMatrix[this.activeKey][0] !== "0x00") {
        keyMatrix[this.activeKey] = ["0x00", "0xe0", "0x00", "0x04"];
      }

      keyMatrix[this.activeKey][index] = value ?? "0x00";
      this.$store.dispatch("mouseAM35/setKeyMatrix", { ...keyMatrix });
    },
    /**
     * 处理宏按键选择事件
     * 当用户选择一个宏时触发
     */
    onSelectedMacro(e) {
      if (!this.activeKey) return;

      const macroName = e.target.value;
      this.updateMacroKeyMapping(macroName);
    },

    /**
     * 更新宏按键映射配置
     * @param {string} macroName - 宏名称
     * 
     * 协议格式: [8, repeat_mode, repeat_times, macro_name, [keyOrder数组]]
     */
    updateMacroKeyMapping(macroName) {
      // 更新本地状态
      this.selectedKey = "8";
      this.selectedMacroName = macroName;

      // 查找对应的宏数据
      const macro = this.findMacroByName(macroName);
      if (!macro) {
        console.warn("未找到匹配的宏: " + macroName);
        this.$message.error(`未找到匹配的宏: ${macroName}`);
        return;
      }

      // 更新宏的重复模式配置
      this.updateMacroRepeatSettings(macroName);

      // 构建宏按键映射：[8, repeat_mode, repeat_times, macro_name, keyOrder数组]
      const macroMapping = [
        8,
        this.repeatMode,
        this.repeatTimes,
        macroName,
        macro.macroValue?.keyOrder || []
      ];
      this.updateStoreKeyMatrix(this.activeKey, macroMapping);

      // 同步配置到设备
      this.syncKeyConfigurationToDevice();
    },

    /**
     * 更新宏的重复设置
     * @param {string} macroName - 宏名称
     */
    updateMacroRepeatSettings(macroName) {
      const activeKeyMapping = this.activeKey ? this.keyMatrix?.[this.activeKey] : null;
      if (
        activeKeyMapping &&
        (activeKeyMapping[0] === 8 || activeKeyMapping[0] === "8") &&
        activeKeyMapping[3] === macroName
      ) {
        this.repeatMode = Number(activeKeyMapping[1] ?? 0);
        this.repeatTimes = Number(activeKeyMapping[2] ?? 1);
        this.repeatTimesInput = String(this.repeatTimes);
        const activeMacro =
          this.findUiMacroByName(macroName) || this.findMacroByName(macroName);
        this.macroLoopInterval = this.normalizeMacroLoopIntervalValue(
          activeMacro?.setAM35MacroLoopInterval?.interval,
          50
        );
        this.macroLoopIntervalInput = String(this.macroLoopInterval);
        return;
      }

      const macro =
        this.findUiMacroByName(macroName) || this.findMacroByName(macroName);
      if (macro) {
        this.repeatMode = Number(macro.repeat_mode ?? 0);
        this.repeatTimes = Number(macro.repeat_times ?? 1);
      } else {
        this.repeatMode = 0;
        this.repeatTimes = 1;
      }
      this.repeatTimesInput = String(this.repeatTimes);
      this.macroLoopInterval = this.normalizeMacroLoopIntervalValue(
        macro?.setAM35MacroLoopInterval?.interval,
        50
      );
      this.macroLoopIntervalInput = String(this.macroLoopInterval);
    },
    normalizeRepeatTimesValue(value, fallback = 1) {
      const numericValue = Number.parseInt(String(value ?? "").replace(/[^0-9]/g, ""), 10);
      if (!Number.isFinite(numericValue)) {
        return fallback;
      }
      if (numericValue < 1) {
        return 1;
      }
      if (numericValue > 65535) {
        return 65535;
      }
      return numericValue;
    },
    normalizeMacroLoopIntervalValue(value, fallback = 50) {
      const numericValue = Number.parseInt(
        String(value ?? "").replace(/[^0-9]/g, ""),
        10
      );
      if (!Number.isFinite(numericValue)) {
        return fallback;
      }
      if (numericValue < 1) {
        return 1;
      }
      if (numericValue > 65535) {
        return 65535;
      }
      return numericValue;
    },
    updateSelectedMacroLoopInterval(intervalValue) {
      if (!this.selectedMacroName) return;

      const mouseMacroData = [...this.mouseMacroData];
      const macroIndex = mouseMacroData.findIndex(
        (item) => item.macro_name === this.selectedMacroName
      );

      if (macroIndex === -1) return;

      const currentLoopInterval =
        mouseMacroData[macroIndex].setAM35MacroLoopInterval || {};
      mouseMacroData[macroIndex].setAM35MacroLoopInterval = {
        keyID: Number(currentLoopInterval.keyID ?? 0),
        interval: this.normalizeMacroLoopIntervalValue(intervalValue, 50),
      };
      this.$store.dispatch("mouseAM35/setMouseMacroData", mouseMacroData);
    },
    /**
     * 处理宏重复模式变更事件
     * repeat_mode: 0=重复N次, 1=按住触发, 2=自动循环
     * 当 repeat_mode 非 0 时，repeat_times 应该为 0
     */
    onSelectedRepeatMode(e) {
      this.repeatMode = Number(e.target.value);
      // 当 repeat_mode 非 0 时（按住触发/自动循环），repeat_times 设为 0
      this.repeatTimes = this.repeatMode === 0 ? 1 : 0;
      this.repeatTimesInput = String(this.repeatTimes);
      
      // 更新 keyMatrix 中的宏参数
      this.updateMacroKeyMatrixParameters();
      
      this.syncKeyConfigurationToDevice();
    },
    onRepeatTimesFocus() {
      if (this.repeatMode !== 0) {
        this.onSelectedRepeatMode({ target: { value: 0 } });
      }
    },
    onRepeatTimesInput(e) {
      const inputValue = String(e.target.value ?? "").replace(/[^0-9]/g, "");
      this.repeatTimesInput = inputValue;
    },
    onMacroLoopIntervalInput(e) {
      const inputValue = String(e.target.value ?? "").replace(/[^0-9]/g, "");
      this.macroLoopIntervalInput = inputValue;
    },
    onMacroLoopIntervalChange(e) {
      const nextInterval = this.normalizeMacroLoopIntervalValue(
        e.target.value,
        this.macroLoopInterval
      );
      this.macroLoopInterval = nextInterval;
      this.macroLoopIntervalInput = String(nextInterval);
      this.updateSelectedMacroLoopInterval(nextInterval);
      this.syncKeyConfigurationToDevice();
    },

    /**
     * 处理宏重复次数变更事件
     */
    onRepeatTimesChange(e) {
      const repeatTimes = this.normalizeRepeatTimesValue(e.target.value, this.repeatTimes || 1);
      this.repeatTimes = repeatTimes;
      this.repeatTimesInput = String(repeatTimes);
      
      // 更新 keyMatrix 中的宏参数
      this.updateMacroKeyMatrixParameters();
      
      this.syncKeyConfigurationToDevice();
    },

    /**
     * 更新 keyMatrix 中的宏参数
     * 当 repeat_mode 或 repeat_times 变化时调用
     */
    updateMacroKeyMatrixParameters() {
      if (!this.activeKey || !this.selectedMacroName) return;
      
      const macro = this.findMacroByName(this.selectedMacroName);
      if (!macro) return;

      // 更新 keyMatrix: [8, repeat_mode, repeat_times, macro_name, keyOrder数组]
      const macroMapping = [
        8,
        this.repeatMode,
        this.repeatTimes,
        this.selectedMacroName,
        macro.macroValue?.keyOrder || []
      ];
      
      this.updateStoreKeyMatrix(this.activeKey, macroMapping);
    },
    /**
     * 禁用按键
     * 将当前选中的按键设置为禁用状态
     */
    onDisable() {
      const disabledMapping = ["0"];
      this.selectedKey = "0";
      this.updateStoreKeyMatrix(this.activeKey, disabledMapping);
      this.syncKeyConfigurationToDevice();
    },
    clearMacroSelectionState() {
      this.selectedMacroName = "";
      this.repeatMode = 0;
      this.repeatTimes = 1;
      this.repeatTimesInput = "1";
      this.macroLoopInterval = 50;
      this.macroLoopIntervalInput = "50";
    },

    /**
     * 恢复按键默认设置
     * 将当前选中的按键恢复为默认的原生鼠标按键功能
     */
    onRestore() {
      const defaultKeyMatrix = {
        "0x01": ["1"],
        "0x02": ["2"],
        "0x03": ["3"],
        "0x04": ["4"],
        "0x05": ["5"],
        "0x06": ["12"],
        "0x07": ["13"],
      };

      const defaultMapping = defaultKeyMatrix[this.activeKey];
      if (defaultMapping) {
        this.selectedKey = defaultMapping.toString();
        this.clearMacroSelectionState();
        this.updateStoreKeyMatrix(this.activeKey, defaultMapping);
        this.syncKeyConfigurationToDevice();
      }
    },

    /**
     * 重置按钮点击
     */
    onReset() {
      this.$confirm({
        title: this.$t("mouse.reset"),
        okText: this.$t("yes"),
        cancelText: this.$t("cancel"),
        centered: true,
        onOk: () => this.startStepLoader("reset"),
      });
    },
    onSwitchProfile(profileValue) {
      const nextProfile = Number(profileValue);
      if (this.isProfileSwitching || nextProfile === this.selectedAM35Profile) {
        return;
      }
      this.startStepLoader("switchProfile", { profileValue: nextProfile });
    },

    onUpload() {
      setTimeout(() => {
        if (this.type === "upload") {
          this.type = "keymap";
        }
      }, 10);

      if (this.isWeb || this.isQT) {
        this.startStepLoader("import");
        return;
      }

      this.pauseAM35BatteryGuard("import");
      this.$store.dispatch("loader/setLoading", { loading: true, time: 30000 });
      setTimeout(() => {
        this.$store.dispatch("loader/loadAM35json");
      }, 100);
    },
    onDownload() {
      setTimeout(() => (this.type = ""), 10);
      this.selectVisible = false;
      this.pauseAM35BatteryGuard("export");
      
      // 构建导出数据
      const saveData = {
        KeyMatrix: this.selectKeymap ? this.keyMatrix : null,
        mouseArgs: this.selectParam ? this.mouseArgs : null,
        lightData: this.selectParam ? this.lightData : null,
        mouseMacroData: this.selectMacro ? this.mouseMacroData : null,
      };
      console.log(saveData, "saveData");
      
      // Qt 模式：使用原来的 loading（文件选择窗口会阻塞 webview）
      // 网页模式：使用 StepLoader 显示导出进度
      if (this.isWeb) {
        this.startStepLoader("export", { saveData });
      } else {
        this.$store.dispatch("loader/setLoading", true);
        this.$store.dispatch("loader/saveAM35json", saveData);
      }
    },
    // ==================== WebSocket 回调处理 ====================
    onHostInitAM35Data(json) {
      console.log("onHostInitAM35Data", json);
      this.clearSceneFallbackTimer("importCancelFallbackTimer");
      
      // 清除初始化超时定时器（已成功收到回调）
      this.clearInitTimeout();
      const isImportStepLoaderActive = this.isImportStepLoaderActive();
      if (isImportStepLoaderActive) {
        this.importResult = "success";
        this.clearImportFlowTimeout();
      }
      
      const data = JSON.parse(json);
      if (data) {
        this.$store.dispatch("mouseAM35/setMouseArgs", {
          reportRate: Number(data?.mouseArgs?.reportRate ?? 8000),
          currentID: Number(data?.mouseArgs?.currentID ?? 0),
          currentProfile:
            data?.mouseArgs?.currentProfile ?? data?.mouseArgs?.CurrentProfile,
          xDPI: data?.mouseArgs?.xDPI?.map(Number) ?? [
            800,
            1600,
            2400,
            3200,
            5000,
            10000,
            20000,
            26000,
          ],
          yDPI: data?.mouseArgs?.yDPI?.map(Number) ?? [
            800,
            1600,
            2400,
            3200,
            5000,
            10000,
            20000,
            26000,
          ],
          DPIcolor: data?.mouseArgs?.DPIcolor ?? [
            "#FF0000",
            "#00FF00",
            "#0000FF",
            "#00FFFF",
            "#800080",
            "#FFFF00",
            "#FF00FF",
            "#ffffff",
          ],
          DPIsyn: data?.mouseArgs?.DPIsyn ?? 1,
          DPIStages: data?.mouseArgs?.DPIStages, // DPI档位数量，undefined表示使用默认8档
          getDPIButton: data?.mouseArgs?.getDPIButton ?? 1, // DPI按键状态
          // 去抖：新版仅使用三路字段
          wireDebounce: Number(data?.mouseArgs?.wireDebounce ?? 255),
          rf24gDebounce: Number(data?.mouseArgs?.rf24gDebounce ?? 255),
          btDebounce: Number(data?.mouseArgs?.btDebounce ?? 255),
          MotionSync: data?.mouseArgs?.MotionSync ?? 1,
          LOD: data?.mouseArgs?.LOD ?? 1,
          AngleSnapping: data?.mouseArgs?.AngleSnapping ?? 1,
          RippleCtrl: data?.mouseArgs?.RippleCtrl ?? 1,
          AM35FPStatus: data?.mouseArgs?.AM35FPStatus ?? 0,
          AM35rotateAngleSwitch:
            data?.mouseArgs?.AM35rotateAngleSwitch == null
              ? null
              : Number(data?.mouseArgs?.AM35rotateAngleSwitch),
          AM35ProfileKeySwitch:
            data?.mouseArgs?.AM35ProfileKeySwitch == null
              ? null
              : Number(data?.mouseArgs?.AM35ProfileKeySwitch),
          setAM35rotateAngle:
            data?.mouseArgs?.setAM35rotateAngle == null
              ? null
              : Number(data?.mouseArgs?.setAM35rotateAngle),
          CurrentProfile: data?.mouseArgs?.CurrentProfile ?? "1",
          sleepTime24G: data?.mouseArgs?.sleepTime24G ?? 30,
          sleepTimeBT: data?.mouseArgs?.sleepTimeBT ?? 600,
        });
        
        // 处理 KeyMatrix 数据，转换键盘按键类型中的空值为空字符串
        const processedKeyMatrix = parseKeyMatrixFromDevice(data?.KeyMatrix);
        const processedMouseMacroData = mergeMouseMacroDataFromKeyMatrix(
          processedKeyMatrix,
          data?.mouseMacroData
        );
        this.$store.dispatch("mouseAM35/setKeyMatrix", processedKeyMatrix);
        // 从 data.lightData 获取灯光数据
        const lightData = {};

        // 如果存在 lightData 对象，则复制其中的所有键值对
        if (data?.lightData) {
          // 遍历 data.lightData 中的所有键值对
          Object.keys(data.lightData).forEach((key) => {
            // 数值类型的属性需要转换为数字
            if (
              [
                "dongleSaturationPer",
                "dongleSpeed",
                "dongleBrightness",
                "mouseLightSpeed",
              ].includes(key) &&
              data?.lightData?.[key] != null
            ) {
              lightData[key] = Number(data.lightData[key]);
            } else if (data?.lightData?.[key] != null) {
              // 其他类型直接复制
              lightData[key] = data.lightData[key];
            }
          });
        }

        this.$store.dispatch("mouseAM35/setLight", lightData);
        this.$store.dispatch(
          "mouseAM35/setMouseMacroData",
          processedMouseMacroData
        );
        this.requestAM35ProfileKeySwitch();
        
        // 等待 Store 更新完成后再更新本地状态
        this.$nextTick(() => {
          this.refreshKeymapSelection({
            // 初始化后即使当前键临时处于禁点态，也要按设备回包刷新右侧回显。
            force: true,
          });
          if (isImportStepLoaderActive) {
            this.completeStepLoader("import");
            return;
          }
          this.resumeAM35BatteryGuard("import", { refresh: true, force: true });
        });
        
        this.$store.dispatch("loader/setLoading", false);
        console.log("initAM35Data", "true");
      }
    },
    onHostLoadingFinish(payload) {
      if (
        this.handleSceneLoadingFinish({
          payload,
          sceneName: "import",
          timerKey: "importCancelFallbackTimer",
          isSceneActive: () => this.isImportStepLoaderActive(),
          cancelLog: "cancel import due to loadingFinish",
          ignoreLog: "ignore loadingFinish during import",
          onCancel: () => {
            this.clearImportFlowTimeout();
            this.closeStepLoader();
            this.resumeAM35BatteryGuard("import", {
              refresh: true,
              force: true,
            });
            this.$store.dispatch("loader/setLoading", false);
          },
        })
      ) {
        return;
      }
      this.resumeAM35BatteryGuard("import");
      this.resumeAM35BatteryGuard("export", { refresh: true, force: true });
      this.$store.dispatch("loader/setLoading", false);
      console.log("loadingFinish");
    },
    onHostSetSystemInfo() {
      if (!this.isImportStepLoaderActive()) {
        return;
      }
      this.setStepLoaderStep(1);
      console.log("import setSystemInfo");
    },
    onHostGetAM35ProfileKeySwitch(json) {
      console.log("onHostGetAM35ProfileKeySwitch", json);
      const data = JSON.parse(json);
      if (data && data.AM35ProfileKeySwitch != null) {
        this.$store.dispatch("mouseAM35/setAM35ProfileKeySwitch", {
          AM35ProfileKeySwitch: Number(data.AM35ProfileKeySwitch),
        });
      }
    },
    
    onHostUpdateDPIButton(json) {
      console.log("onHostUpdateDPIButton", json);
      const data = JSON.parse(json);
      if (data && data.setDPIButton !== undefined) {
        this.$store.dispatch("mouseAM35/setMouseArgs", {
          ...this.mouseArgs,
          setDPIButton: Number(data.setDPIButton),
        });
      }
    },
    // ==================== Host 事件处理 ====================
    subscribeToHostEvents() {
      deviceCallbackManager.subscribe("initAM35Data", this.onHostInitAM35Data);
      deviceCallbackManager.subscribe(
        "getAM35ProfileKeySwitch",
        this.onHostGetAM35ProfileKeySwitch
      );
      deviceCallbackManager.subscribe("loadingFinish", this.onHostLoadingFinish);
      deviceCallbackManager.subscribe("resetAM35", this.onHostResetAM35);
      deviceCallbackManager.subscribe("setSystemInfo", this.onHostSetSystemInfo);
    },
    unsubscribeFromHostEvents() {
      deviceCallbackManager.unsubscribe("initAM35Data", this.onHostInitAM35Data);
      deviceCallbackManager.unsubscribe(
        "getAM35ProfileKeySwitch",
        this.onHostGetAM35ProfileKeySwitch
      );
      deviceCallbackManager.unsubscribe("loadingFinish", this.onHostLoadingFinish);
      deviceCallbackManager.unsubscribe("resetAM35", this.onHostResetAM35);
      deviceCallbackManager.unsubscribe("setSystemInfo", this.onHostSetSystemInfo);
      deviceCallbackManager.unsubscribe("initAM35Data", this._onSwitchProfileInitComplete);
    },
  },
  watch: {
    type(type) {
      if (type === "keymap") {
        this.refreshKeymapSelection({ force: true });
      }
    },
    keyMatrix() {
      this.refreshKeymapSelection();
    },
    macroData() {
      this.refreshKeymapSelection();
    },
    mouseMacroData() {
      this.refreshKeymapSelection();
    },
  },
  mounted() {
    this.$store.dispatch("loader/setInLoader", true);
    this.$store.dispatch("loader/initAM35Data");
    this.$store.dispatch("loader/setLoading", {time:15000,loading:true});

    this.subscribeToHostEvents();

    // 启动初始化数据超时监控
    this.startInitTimeout();

  },

  destroyed() {
    this.unsubscribeFromHostEvents();
    this.cleanupStepLoaderOnDestroy();
    
    // 清理初始化超时定时器
    this.clearInitTimeout();
    this.clearImportFlowTimeout();
    this.clearSceneFallbackTimer("importCancelFallbackTimer");
  },
};
</script>
<style lang="scss" scoped>
.mouse {
  font-size: 16px;
  color: #ffffff;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 150px;
  background: #171717;

  .fixed-panel.px {
    width: 100%;
    background: #171717;
    padding: 60px 0 20px 0;
    position: fixed;
    z-index: 1;
    top: 0;
    
    /* 屏幕高度不足时减小padding，但保持固定定位 */
    @media screen and (max-height: 820px) {
      padding: 60px 0 0 0;
    }
  }

  .mouse-panel {
    @include flex-center;

    .keys {
      width: 400px;
      height: 400px;
      // background: url('../../assets/images/device_am35_m_a.png') center / auto 100% no-repeat;
      position: relative;

      .key {
        width: 300px;
        height: 35px;
        line-height: 35px;
        position: absolute;
        font-size: 20px;
        border-bottom: 1px solid #bf94ff;
        opacity: 0.5;
        white-space: nowrap;
        cursor: pointer;

        &:hover,
        &.active {
          opacity: 1;
          color: $primary-color;
          font-size: 24px;
        }

        &:hover {
          background-color: #ffffff11;
        }

        &.disabled {
          cursor: not-allowed;
        }

        &::before {
          display: block;
          content: "";
          width: 50px;
          height: 0;
          border-top: 1px solid #bf94ff;
          transform: rotate(45deg);
          position: absolute;
          right: 0;
          bottom: 0;
        }

        &::after {
          display: block;
          content: "";
          width: 8px;
          height: 8px;
          border-radius: 8px;
          background: #bf94ff;
          position: absolute;
          right: 0;
          bottom: 0;
        }
      }

      .key1 {
        top: -5%;
        left: -47%;

        &::before {
          right: -43px;
          bottom: -18px;
        }

        &::after {
          right: -43px;
          bottom: -43px;
        }
      }

      .key3 {
        top: 14%;
        left: 74%;
        text-align: right;

        &::before {
          width: 100px;
          transform: rotate(0);
          left: -100px;
          bottom: -2px;
        }

        &::after {
          left: -100px;
          bottom: -4px;
        }
      }

      .key2 {
        top: -5%;
        left: 74%;
        text-align: right;

        &::before {
          transform: rotate(135deg);
          left: -43px;
          bottom: -18px;
        }

        &::after {
          left: -43px;
          bottom: -43px;
        }
      }

      .key4 {
        top: 33%;
        left: -62%;

        &::before {
          transform: rotate(0);
          right: -50px;
          bottom: -1px;
        }

        &::after {
          right: -50px;
          bottom: -4px;
        }
      }

      .key5 {
        top: 50%;
        left: -62%;

        &::before {
          transform: rotate(0);
          right: -50px;
          bottom: -1px;
        }

        &::after {
          right: -50px;
          bottom: -4px;
        }
      }

      .key6 {
        top: 17%;
        left: -34.5%;

        &::before {
          transform: rotate(135deg);
          right: -43px;
          bottom: 17px;
        }

        &::after {
          right: -41px;
          bottom: 30px;
        }
      }

      .key7 {
        top: 30%;
        left: 60%;
        text-align: right;

        &::before {
          left: -43px;
          bottom: 17px;
        }

        &::after {
          left: -44px;
          bottom: 32px;
        }
      }
    }
  }

  .radio-panel {
    @include flex-center;
    width: 1200px;
    margin: 0 auto 0 auto;
    justify-content: flex-end;
    align-items: center;
    white-space: nowrap;
    gap: 6px;

    .tab-group {
      display: flex;
      align-items: center;
      height: 40px;
      padding: 0;
      border: 1px solid $primary-color;
      border-radius: 10px;
      overflow: hidden;
      background: transparent;
    }

    .virtual-radio {
      display: flex;
      justify-content: flex-start;

      #file {
        display: none;
      }
    }

    .function-tabs {
      flex: 0 0 auto;
    }

    .profile-action-tabs {
      flex: 0 0 auto;
      gap: 0;
    }

    .action-radio {
      flex: 0 0 auto;
    }

    .profile-dropdown {
      flex: 0 0 auto;
    }

    .profile-trigger {
      @include flex-center;
      min-width: 94px;
      height: 38px;
      padding: 0 12px;
      color: $primary-color;
      cursor: pointer;
      user-select: none;
      background: transparent;

      &.disabled {
        opacity: 0.45;
        cursor: not-allowed;
      }
    }

    .tab-trigger {
      border-right: 1px solid $primary-color;
    }

    .profile-trigger-label {
      font-size: 17px;
      line-height: 1;
    }

    .profile-trigger-arrow {
      font-size: 10px;
      margin-left: 6px;
      transform: none;
    }

    .ant-radio-group {
      .ant-radio-button-wrapper {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        height: 38px;
        padding: 0 14px;
        line-height: 38px;
        border-top: 0;
        border-bottom: 0;

        &:first-child {
          border-left: 0;
          border-radius: 0;
        }

        &:last-child {
          border-right: 0;
          border-radius: 0;
        }

        &.ant-radio-button-wrapper-checked {
          box-shadow: none;
        }
      }
    }
  }

  .settings-panel {
    @include flex-center;
    margin-top: calc(500px - 4vw);
    margin-bottom: 60px;
    position: relative;
    z-index: 0;
  }

  .panels {
    @include flex-center;
    flex-wrap: wrap;
    width: 1500px;

    .buttons {
      @include flex-center;
      width: 400px;
      height: 100px;

      .button {
        @include button;
        width: 180px;
        height: 34px;
        margin: 10px;
        border: 1px solid $primary-color;
        border-radius: 5px;
        background: transparent;
        color: $primary-color;

        &:hover {
          color: #ffffff;
        }
      }
    }
  }

  .panel {
    width: 370px;
    height: 340px;
    background-image: linear-gradient(270deg, #ee92ff 0%, #42bfe9 100%);
    box-shadow: 0 0 20px 0 #4136f1;
    border-radius: 15px;
    padding: 3px;
    margin: 0 20px;
    /* 兼容性修复：开启GPU加速，优化渲染层级 */
    transform: translateZ(0);
    will-change: transform;

    .panel-content {
      width: 100%;
      height: 100%;
      padding: 5px 15px;
      background-color: #000000;
      border-radius: 15px;
      /* 兼容性修复：防止背景色溢出覆盖父元素padding区域 */
      background-clip: padding-box;
      display: flex;
      flex-direction: column;
      position: relative;

      .title {
        @include flex-center;
        width: 100%;
        font-size: 24px;
        line-height: 50px;
        height: 50px;
        color: $primary-color;
        flex: none;

        .half {
          width: 50%;
          height: 42px;
          text-align: center;
          color: #999999;
          cursor: pointer;

          &:hover {
            color: $primary-color;
          }
        }

        .active {
          color: $primary-color;
          border-bottom: 2px solid $primary-color;
        }
      }
    }

    .radio-group {
      width: 100%;
      height: 100%;
      overflow-y: scroll;
      flex: auto;

      &::-webkit-scrollbar {
        width: 10px;
      }

      .radio {
        @include flex-center;
        height: 50px;
        line-height: 50px;
        justify-content: flex-start;
        font-size: 20px;

        &.ant-radio-wrapper-checked,
        &:hover {
          color: #ffffff;
        }
      }
    }

    .play-model {
      @include flex-center;
      height: 60px;
      color: #ffffff;
      flex: none;

      .radio {
        @include flex-center;
        justify-content: flex-start;
        margin: 0 10px;
      }
    }

    &.hotkey {
      .panel-content {
        flex-direction: row;
        flex-wrap: wrap;
        align-items: center;
        justify-content: space-between;
        align-content: flex-start;
        color: $primary-color;
        font-size: 16px;

        .sub-title {
          width: 100%;
          font-size: 20px;
          color: $primary-color;
          flex: none;
          margin-bottom: -10px;
        }
      }

      .radio-group {
        width: 100px;
        height: auto;
        overflow-y: auto;
        flex: none;
      }

      .checkbox-group {
        display: flex;
        flex-direction: column;
      }

      .radio,
      .checkbox {
        @include flex-center;
        height: 40px;
        line-height: 40px;
        justify-content: flex-start;
        background-color: #050505;
        color: #ffffff;
        padding: 0 5px;
        margin: 5px 5px 0 5px;
        border-radius: 5px;
        font-size: 18px;
      }
    }
  }
}
</style>

<style lang="scss">
.mouse {
  .panel {
    .radio-group .radio {
      .ant-radio .ant-radio-inner {
        border-color: $primary-color;

        &::after {
          background-color: $primary-color;
        }
      }
    }

    &.keybord .virtual-key {
      margin: 80px auto 0 auto;
    }

    &.hotkey {
      .radio-group .radio {
        .ant-radio {
          margin-right: 0;
        }
      }
      .md-checkbox {
        .md-checkbox-container {
          border-radius: 2px;
          border-color: $primary-color;
        }

        label {
          padding-left: 5px;
        }
      }
      .md-checkbox.md-theme-default.md-checked .md-checkbox-container {
        background-color: $primary-color;
      }
    }

    .play-model .radio {
      .ant-radio {
        margin-right: 5px;
      }

      span:not(.ant-radio) {
        @include flex-center;
        padding: 0;
        font-size: 14px;
        color: #ffffff;
      }
    }

    .repeat {
      width: 60px;
      margin-left: 5px;
      font-size: 14px;
    }

  }
}

.c-select {
  @include flex-center;
  width: 900px;
  flex-direction: column;
  margin: 0 auto;
  position: relative;

  .title {
    font-size: 24px;
    color: #ffffff;
    margin-bottom: 30px;
  }

  .checkbox {
    width: 150px;
    font-size: 20px;
    color: #ffffff;
    margin: 0 auto 30px auto;
    text-align: left;
    white-space: nowrap;
  }

  .buttons {
    @include flex-center;
    width: 100%;
    margin-top: 10px;

    .button {
      width: 150px;
      height: 53px;
      background-color: $active-color;
      border-radius: 5px;
      font-size: 20px;
      text-transform: none;

      & + .button {
        margin-left: 100px;
      }
    }
  }

  &.cn .checkbox {
    width: 160px;
  }
}

.am35-profile-menu {
  min-width: 180px;
  background: #171717;
  border: 1px solid #bf94ff;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
}

.am35-profile-switch-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 14px;
  border-bottom: 1px solid rgba(191, 148, 255, 0.2);
  color: $primary-color;

  &.disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
}

.am35-profile-switch-label {
  font-size: 16px;
  line-height: 1.4;
}

.am35-profile-menu-item {
  display: flex;
  align-items: center;
  padding: 10px 14px;
  font-size: 17px;
  line-height: 1.4;
  color: #ffffff;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover,
  &.active {
    background: rgba(191, 148, 255, 0.16);
    color: $primary-color;
  }

  &.disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
}

.am35-profile-menu-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex: none;
  margin-right: 12px;

  &.green {
    background: #57d17b;
  }

  &.yellow {
    background: #f4d35e;
  }

  &.red {
    background: #ff5a5f;
  }
}

.am35-profile-menu-text {
  white-space: nowrap;
}

.am35-profile-dropdown {
  .ant-dropdown {
    transform-origin: top center;
    will-change: transform, opacity;
  }

  .am35-profile-menu {
    min-width: 180px;
  }

  .md-switch.md-theme-default.md-checked.md-primary .md-switch-container {
    background-color: $primary-color;
  }
}

.am35-profile-dropdown.ant-dropdown.slide-up-enter,
.am35-profile-dropdown.ant-dropdown.slide-up-appear {
  opacity: 0;
  transform: scaleY(0.92);
}

.am35-profile-dropdown.ant-dropdown.slide-up-enter.slide-up-enter-active,
.am35-profile-dropdown.ant-dropdown.slide-up-appear.slide-up-appear-active {
  animation-duration: 0.2s;
}

.am35-profile-dropdown.ant-dropdown.slide-up-leave {
  opacity: 1;
  transform: scaleY(1);
}

.am35-profile-dropdown.ant-dropdown.slide-up-leave.slide-up-leave-active {
  animation-duration: 0.18s;
}
</style>
